/** The small component set of the app (no UI kit): buttons, fields, cards, badges, switches, modal, toasts. */
import { AlertTriangle, Check, CheckCircle2, Copy, Info, X, XCircle } from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
} from "react";

export function cx(...names: (string | false | null | undefined)[]): string {
  return names.filter(Boolean).join(" ");
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "ghost" | "danger" | "default";
  size?: "sm" | "lg";
  loading?: boolean;
  icon?: ReactNode;
  block?: boolean;
};

export function Button({ variant = "default", size, loading, icon, block, className, children, disabled, ...rest }: ButtonProps) {
  return (
    <button
      type="button"
      className={cx("btn", variant !== "default" && `btn-${variant}`, size && `btn-${size}`, block && "btn-block", className)}
      disabled={disabled || loading}
      {...rest}
    >
      {loading ? <span className="spinner" style={{ width: 14, height: 14 }} /> : icon}
      {children}
    </button>
  );
}

export function Card({ title, description, actions, children, footer, className, pad }: {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  className?: string;
  pad?: boolean;
}) {
  return (
    <section className={cx("card", className)}>
      {(title || actions) && (
        <header className="card-head">
          <div>
            {title && <h2>{title}</h2>}
            {description && <p>{description}</p>}
          </div>
          {actions && <div className="row">{actions}</div>}
        </header>
      )}
      {pad === false ? children : <div className="card-body">{children}</div>}
      {footer && <footer className="card-foot">{footer}</footer>}
    </section>
  );
}

export function PageHead({ title, description, actions, crumbs }: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  crumbs?: ReactNode;
}) {
  return (
    <div className="page-head">
      <div>
        {crumbs && <div className="crumbs">{crumbs}</div>}
        <h1>{title}</h1>
        {description && <p>{description}</p>}
      </div>
      {actions && <div className="actions">{actions}</div>}
    </div>
  );
}

export function Field({ label, hint, error, children, className }: {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cx("field", className)}>
      {label && <label>{label}</label>}
      {children}
      {error ? <span className="field-error">{error}</span> : hint ? <span className="hint">{hint}</span> : null}
    </div>
  );
}

export function TextInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input className="input" {...props} />;
}

/** A number input bound to a number (empty = null). */
export function NumberInput({ value, onChange, suffix, step = "any", min, max, disabled, placeholder }: {
  value: number | null | undefined;
  onChange: (value: number | null) => void;
  suffix?: string;
  step?: number | string;
  min?: number;
  max?: number;
  disabled?: boolean;
  placeholder?: string;
}) {
  const [text, setText] = useState(value == null ? "" : String(value));
  useEffect(() => {
    if (value == null ? text !== "" : Number(text) !== value) setText(value == null ? "" : String(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  const input = (
    <input
      className="input"
      type="number"
      inputMode="decimal"
      step={step}
      min={min}
      max={max}
      disabled={disabled}
      placeholder={placeholder}
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        onChange(e.target.value === "" ? null : Number(e.target.value));
      }}
    />
  );
  return suffix ? (
    <div className="input-affix">
      {input}
      <span>{suffix}</span>
    </div>
  ) : (
    input
  );
}

export function Select<T extends string>({ value, onChange, options, disabled }: {
  value: T | null | undefined;
  onChange: (value: T) => void;
  options: readonly (T | { value: T; label: string })[];
  disabled?: boolean;
}) {
  return (
    <select className="select" value={value ?? ""} disabled={disabled} onChange={(e) => onChange(e.target.value as T)}>
      {options.map((o) => {
        const v = typeof o === "string" ? o : o.value;
        const l = typeof o === "string" ? o : o.label;
        return (
          <option key={v} value={v}>
            {l}
          </option>
        );
      })}
    </select>
  );
}

export function Switch({ checked, onChange, disabled, label }: {
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
  label?: ReactNode;
}) {
  return (
    <label className="switch">
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span className="track" />
      {label}
    </label>
  );
}

export function Badge({ tone, children, plain }: { tone?: "ok" | "warn" | "danger" | "info" | "grad"; children: ReactNode; plain?: boolean }) {
  return <span className={cx("badge", tone, plain && "plain")}>{children}</span>;
}

export function Alert({ tone = "info", title, children, action }: {
  tone?: "info" | "warn" | "danger" | "ok";
  title?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
}) {
  const Icon = tone === "ok" ? CheckCircle2 : tone === "danger" ? XCircle : tone === "warn" ? AlertTriangle : Info;
  return (
    <div className={cx("alert", tone)} role={tone === "danger" ? "alert" : undefined}>
      <Icon />
      <div style={{ flex: 1 }}>
        {title && <b>{title} </b>}
        {children}
      </div>
      {action}
    </div>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="center-fill">
      <div className="row muted">
        <span className="spinner" /> {label ?? "Loading…"}
      </div>
    </div>
  );
}

export function Empty({ icon, title, children, action }: { icon: ReactNode; title: ReactNode; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      <div className="empty-icon">{icon}</div>
      <h3>{title}</h3>
      {children && <p style={{ maxWidth: 420 }}>{children}</p>}
      {action && <div style={{ marginTop: 8 }}>{action}</div>}
    </div>
  );
}

export function useCopy(): [boolean, (text: string) => void] {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number>();
  const copy = useCallback((text: string) => {
    const done = () => {
      setCopied(true);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(false), 1600);
    };
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(text).then(done, done);
    else done();
  }, []);
  return [copied, copy];
}

export function CopyButton({ text, label = "Copy", size = "sm" }: { text: string; label?: string; size?: "sm" | "lg" }) {
  const [copied, copy] = useCopy();
  return (
    <Button size={size} icon={copied ? <Check /> : <Copy />} onClick={() => copy(text)} aria-label={`${label}: ${text.slice(0, 40)}`}>
      {copied ? "Copied" : label}
    </Button>
  );
}

export function CodeBlock({ code }: { code: string }) {
  return (
    <div className="codeblock">
      <pre>{code}</pre>
      <div className="copy">
        <CopyButton text={code} />
      </div>
    </div>
  );
}

export function Modal({ title, onClose, children, footer, width }: {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-modal="true" style={width ? { width: `min(${width}px, 100%)` } : undefined}>
        <header className="card-head">
          <h2>{title}</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Close" style={{ color: "var(--muted)" }}>
            <X />
          </button>
        </header>
        <div className="card-body">{children}</div>
        {footer && <footer className="card-foot">{footer}</footer>}
      </div>
    </div>
  );
}

export function Segmented<T extends string>({ value, onChange, options }: {
  value: T;
  onChange: (value: T) => void;
  options: { value: T; label: ReactNode }[];
}) {
  return (
    <div className="segmented" role="tablist">
      {options.map((o) => (
        <button key={o.value} className={cx(o.value === value && "on")} onClick={() => onChange(o.value)} role="tab" aria-selected={o.value === value}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** A list of short strings edited as chips (origins, countries, regions). */
export function ChipsInput({ values, onChange, placeholder, normalize, validate }: {
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  normalize?: (value: string) => string;
  validate?: (value: string) => string | null;
}) {
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const add = () => {
    const parts = text.split(/[\s,]+/).map((p) => (normalize ? normalize(p) : p.trim())).filter(Boolean);
    if (!parts.length) return;
    for (const p of parts) {
      const problem = validate?.(p);
      if (problem) {
        setError(problem);
        return;
      }
    }
    onChange([...values, ...parts.filter((p) => !values.includes(p))]);
    setText("");
    setError(null);
  };
  return (
    <div className="field">
      <div className="chips">
        {values.map((v) => (
          <span className="chip" key={v}>
            {v}
            <button onClick={() => onChange(values.filter((x) => x !== v))} aria-label={`Remove ${v}`}>
              <X />
            </button>
          </span>
        ))}
        <input
          value={text}
          placeholder={placeholder}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              add();
            } else if (e.key === "Backspace" && !text && values.length) onChange(values.slice(0, -1));
          }}
          onBlur={add}
        />
      </div>
      {error && <span className="field-error">{error}</span>}
    </div>
  );
}

// ------------------------------------------------------------------ toasts
interface Toast {
  id: number;
  text: string;
  error?: boolean;
}
const ToastContext = createContext<(text: string, error?: boolean) => void>(() => undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((text: string, error?: boolean) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, text, error }]);
    window.setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), error ? 6000 : 3000);
  }, []);
  return (
    <ToastContext.Provider value={push}>
      {children}
      <div className="toasts" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={cx("toast", t.error && "error")}>
            {t.error ? <XCircle /> : <CheckCircle2 />}
            {t.text}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
